<template>
  <el-drawer
    :visible="visible"
    direction="rtl"
    size="460px"
    :with-header="false"
    :show-close="false"
    :append-to-body="true"
    :modal="false"
    :lock-scroll="false"
    class="post-detail-drawer"
    @update:visible="$emit('close')"
  >
    <div v-loading="loading" class="drawer-container">
      <template v-if="detail">
        <!-- 常設アカウントヘッダー（スクロールしても閉じるボタンが隠れないよう固定表示する） -->
        <div class="drawer-header-bar">
          <Account :name="accountName" :image="accountImage" symbol="X" :is-display-name="false" />
          <div class="drawer-header-account">
            <span class="drawer-header-account-name">{{ accountName }}</span>
            <span class="drawer-header-account-meta">
              <span v-if="detail.account" class="drawer-header-account-followers">
                フォロワー数: {{ formatNumber(detail.account.followersCount) }}
              </span>
              <span class="drawer-header-account-industry">業界: {{ industryLabel }}</span>
            </span>
          </div>
          <button class="drawer-close-btn" aria-label="閉じる" @click="onClose">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="drawer-body">
          <!-- X 公式ウィジェット埋め込み（プレビュー表示） -->
          <div class="x-widget">
            <div v-if="embedError" class="x-widget-fallback">
              <p class="x-widget-fallback-text">プレビューを表示できませんでした。</p>
              <a v-if="detail.post.postUrl" :href="detail.post.postUrl" target="_blank" rel="noopener noreferrer">
                元の投稿を開く
              </a>
            </div>
            <div v-else ref="embedContainer" class="x-widget-embed"></div>
          </div>

          <!-- エンゲージメント指標 -->
          <div class="drawer-metrics-section">
            <div class="drawer-metrics-grid">
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatNumber(detail.metrics.impressionsCount) }}</span>
                <span class="drawer-metric-label">インプレッション</span>
              </div>
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatNumber(detail.metrics.likesCount) }}</span>
                <span class="drawer-metric-label">いいね</span>
              </div>
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatNumber(detail.metrics.retweetsCount) }}</span>
                <span class="drawer-metric-label">リポスト</span>
              </div>
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatNumber(detail.metrics.quotesCount) }}</span>
                <span class="drawer-metric-label">引用数</span>
              </div>
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatNumber(detail.metrics.repliesCount) }}</span>
                <span class="drawer-metric-label">返信数</span>
              </div>
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatNumber(detail.metrics.reactionsCount) }}</span>
                <span class="drawer-metric-label">反応数</span>
              </div>
              <div class="drawer-metric-item">
                <span class="drawer-metric-value">{{ formatPercent(detail.metrics.reactionsRate) }}</span>
                <span class="drawer-metric-label">反応率</span>
              </div>
            </div>
          </div>

          <!-- キャンペーン情報 -->
          <div class="drawer-campaign-info-section">
            <h4 class="drawer-section-title">キャンペーン情報</h4>
            <div class="drawer-info-list">
              <div class="drawer-info-row">
                <span class="drawer-info-label">キャンペーン形式</span>
                <span class="drawer-info-value">
                  <el-tag
                    size="mini"
                    :class="
                      isAteluSupportedFormat(detail.post.campaignType) ? 'format-tag--primary' : 'format-tag--gray'
                    "
                    disable-transitions
                  >
                    {{ formatCampaignType(detail.post.campaignType) }}
                  </el-tag>
                </span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">インセンティブ</span>
                <span class="drawer-info-value">{{ formatIncentive(detail.post.incentive) }}</span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">当選人数</span>
                <span class="drawer-info-value">
                  {{ detail.post.winnerCount ? `${detail.post.winnerCount}人` : '-' }}
                </span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">キャンペーン開始日</span>
                <span class="drawer-info-value">{{ formatDateTime(detail.post.campaignStartDatetime) }}</span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">キャンペーン終了日</span>
                <span class="drawer-info-value">
                  <template v-if="detail.post.campaignEndDatetime">
                    {{ formatDateTime(detail.post.campaignEndDatetime) }}
                  </template>
                  <el-tooltip
                    v-else
                    content="このキャンペーンは終了日時が収集できませんでした"
                    placement="top"
                    :open-delay="200"
                  >
                    <span class="campaign-period-unknown">-</span>
                  </el-tooltip>
                </span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">開催日数</span>
                <span class="drawer-info-value">
                  <template v-if="detail.post.campaignDays != null">{{ detail.post.campaignDays }}日</template>
                  <el-tooltip
                    v-else
                    content="キャンペーン終了日が収集できなかったため表示できません"
                    placement="top"
                    :open-delay="200"
                  >
                    <span class="campaign-days-unknown">-</span>
                  </el-tooltip>
                </span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">データ取得日時</span>
                <span class="drawer-info-value">{{ formatDateTimeShort(detail.post.lastSyncedAt) }}</span>
              </div>
              <div class="drawer-info-row">
                <span class="drawer-info-label">タグ</span>
                <span class="drawer-info-value">{{ tagsLabel }}</span>
              </div>
            </div>
          </div>

          <!-- トレンド実績 -->
          <div v-if="detail.trends.length" class="drawer-trend-section">
            <h4 class="drawer-section-title">トレンド実績</h4>
            <div class="drawer-trend-keyword-cards">
              <div v-for="(trend, idx) in detail.trends" :key="idx" class="drawer-trend-keyword-card">
                <div class="drawer-trend-keyword-header">
                  <span class="drawer-trend-keyword-name">{{ trend.keyword }}</span>
                  <el-tag
                    v-if="trend.highestRanking <= 3"
                    size="mini"
                    :class="getTrendRankTagClass(trend.highestRanking)"
                    disable-transitions
                  >
                    最高{{ trend.highestRanking }}位
                  </el-tag>
                  <span v-else class="drawer-trend-rank-plain">最高{{ trend.highestRanking }}位</span>
                </div>
                <div class="drawer-trend-keyword-body">
                  <div class="drawer-trend-row">
                    <span class="drawer-trend-label">初回トレンド入り</span>
                    <span class="drawer-trend-value">{{ formatTrendDate(trend.firstTrendedAt) }}</span>
                  </div>
                  <div class="drawer-trend-row">
                    <span class="drawer-trend-label">最高順位到達</span>
                    <span class="drawer-trend-value">{{ formatTrendDate(trend.highestRankedAt) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- キャンペーン詳細 -->
          <div v-if="detail.post.campaignDetail" class="drawer-detail-section">
            <h4 class="drawer-section-title">キャンペーン詳細</h4>
            <p class="drawer-detail-text">{{ detail.post.campaignDetail }}</p>
          </div>
        </div>

        <!-- フッターバー -->
        <div class="drawer-footer">
          <div class="drawer-footer-actions">
            <el-tooltip content="元の投稿を見る" placement="top">
              <button
                class="drawer-footer-icon-btn"
                :disabled="!detail.post.postUrl"
                aria-label="元の投稿を見る"
                @click="openPostUrl"
              >
                <i class="mdi mdi-open-in-new"></i>
              </button>
            </el-tooltip>
            <el-tooltip content="タグを付与" placement="top">
              <button class="drawer-footer-icon-btn" aria-label="タグを付与" @click="onAssignTag">
                <i class="mdi mdi-tag-multiple"></i>
              </button>
            </el-tooltip>
          </div>

          <div class="drawer-pagination">
            <button class="drawer-pagination-arrow" :disabled="!isPrev" aria-label="前の投稿" @click="onPrev">
              <i class="mdi mdi-chevron-left"></i>
            </button>
            <span class="drawer-pagination-current">{{ displayCurrentIndex }}</span>
            <span class="drawer-pagination-separator">/</span>
            <span class="drawer-pagination-total">{{ total }}</span>
            <button class="drawer-pagination-arrow" :disabled="!isNext" aria-label="次の投稿" @click="onNext">
              <i class="mdi mdi-chevron-right"></i>
            </button>
          </div>
        </div>
      </template>
    </div>
  </el-drawer>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, PropType, ref, watch } from 'vue'

import Account from '@/client/components/_molecules/Account.vue'
import { TrackingService } from '@/client/services'
import { type PostDetailResponse } from '@/typings/twitter_benchmark_campaign_posts'

import {
  formatCampaignType,
  formatIncentive,
  getTrendRankTagClass,
  isAteluSupportedFormat
} from '../../utils/campaignLabels'
import {
  formatDateTime,
  formatDateTimeShort,
  formatNumber,
  formatPercent,
  formatTrendDate
} from '../../utils/formatters'
import { loadTweetWidget } from '../../utils/tweetEmbed'

/**
 * X 競合投稿の詳細ドロワー（表示専用）。モーダル背景を持たず下層の一覧を操作可能なまま重ね、プレビューは X 公式ウィジェットで埋め込む。
 */
export default defineComponent({
  name: 'TwBenchmarkPostDetailDrawer',

  components: {
    Account
  },

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    detail: {
      type: Object as PropType<PostDetailResponse['post'] | null>,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    /** 総件数横断での現在位置（0 始まり）。未特定時は -1。表示は currentIndex >= 0 ? currentIndex + 1 : '-' 。 */
    currentIndex: {
      type: Number,
      default: 0
    },
    /** 総件数（現在ページの件数ではない。例: 1978）。 */
    total: {
      type: Number,
      default: 0
    },
    isPrev: {
      type: Boolean,
      default: false
    },
    isNext: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close', 'prev', 'next', 'assign-tag'],

  setup(props, { emit }) {
    const embedContainer = ref<HTMLElement | null>(null)
    const embedError = ref(false)
    const displayCurrentIndex = computed(() => (props.currentIndex >= 0 ? props.currentIndex + 1 : '-'))

    const accountName = computed(() => props.detail?.account?.name ?? props.detail?.account?.screenName ?? '-')

    const accountImage = computed(() => props.detail?.account?.pictureUrl ?? null)

    const industryLabel = computed(() => {
      const industry = props.detail?.industry
      if (!industry) return '-'
      return `${industry.majorCategory} / ${industry.minorCategory}`
    })

    const tagsLabel = computed(() => {
      const tags = props.detail?.tags ?? []
      return tags.length > 0 ? tags.map(tag => tag.name).join(', ') : '-'
    })

    const openPostUrl = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|投稿詳細|元の投稿を見る')
      const url = props.detail?.post.postUrl
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    }

    const onAssignTag = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|投稿詳細|タグを付与')
      if (props.detail) emit('assign-tag', props.detail.post.id)
    }

    const onPrev = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|投稿詳細|前の投稿')
      emit('prev')
    }

    const onNext = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|投稿詳細|次の投稿')
      emit('next')
    }

    const onClose = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|投稿詳細|閉じる')
      emit('close')
    }

    // 表示中の投稿が変わるたびに X 公式ウィジェットを描画し直す。
    const renderEmbed = async (postId: string | undefined): Promise<void> => {
      embedError.value = false
      await nextTick()

      const container = embedContainer.value
      if (!container || !postId) {
        embedError.value = true
        return
      }

      container.innerHTML = ''

      try {
        const widgets = await loadTweetWidget()
        const el = await widgets.createTweet(postId, container, { conversation: 'none', align: 'center' })
        if (!el) embedError.value = true
      } catch {
        embedError.value = true
      }
    }

    watch(
      () => [props.visible, props.detail?.post.postId] as const,
      ([visible, postId]) => {
        if (visible && props.detail) {
          void renderEmbed(postId)
        }
      },
      { immediate: true }
    )

    return {
      embedContainer,
      embedError,
      displayCurrentIndex,
      accountName,
      accountImage,
      industryLabel,
      tagsLabel,
      openPostUrl,
      onAssignTag,
      onPrev,
      onNext,
      onClose,
      formatNumber,
      formatPercent,
      formatDateTime,
      formatDateTimeShort,
      formatTrendDate,
      formatCampaignType,
      formatIncentive,
      isAteluSupportedFormat,
      getTrendRankTagClass
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.post-detail-drawer {
  pointer-events: none;

  :deep(.el-drawer) {
    pointer-events: auto;
    background-color: #fff;
    box-shadow: -2px 0 8px rgb(0 0 0 / 8%);
  }

  :deep(.el-drawer__body) {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0;
    overflow: hidden;
    background-color: #fff;
  }

  // ドロワーは body 直下に append されるため scoped が届かない箇所を :deep で補う。
  :deep(.format-tag--primary) {
    color: $primary-color;
    background-color: rgba($primary-color, 0.12);
    border-color: rgba($primary-color, 0.2);
  }

  :deep(.format-tag--gray) {
    color: $sub-text-color;
    background-color: #f4f4f5;
    border-color: #e9e9eb;
  }

  :deep(.trend-rank-tag--gold) {
    color: #92600a;
    background-color: #fef6e0;
    border-color: #fce9b2;
  }

  :deep(.trend-rank-tag--silver) {
    color: #5a5a5a;
    background-color: #f2f2f2;
    border-color: #d9d9d9;
  }

  :deep(.trend-rank-tag--bronze) {
    color: #7a4510;
    background-color: #fdf0e6;
    border-color: #f0d4b8;
  }
}

.drawer-container {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.drawer-close-btn {
  flex-shrink: 0;
  padding: 4px;
  margin-left: auto;
  font-size: 18px;
  color: $sub-text-color;
  cursor: pointer;
  background: none;
  border: none;

  &:hover {
    color: $text-color;
  }
}

.drawer-body {
  flex: 1;
  padding: $spacing-unit * 2;
  overflow-y: auto;
}

.drawer-header-bar {
  display: flex;
  flex-shrink: 0;
  gap: $spacing-unit * 1.5;
  align-items: center;
  padding: $spacing-unit * 2;
  background-color: #fff;
  border-bottom: 1px solid $border-color;
}

.drawer-header-account {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.drawer-header-account-name {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: bold;
  color: $text-color;
  white-space: nowrap;
}

.drawer-header-account-meta {
  display: flex;
  flex-direction: column;
  font-size: $font-small-size;
  color: $text-color;
}

.drawer-header-account-followers,
.drawer-header-account-industry {
  font-size: $font-small-size;
  color: $text-color;
}

// X 公式ウィジェット埋め込みラッパー
.x-widget {
  min-height: 80px;
}

.x-widget-embed {
  :deep(.twitter-tweet) {
    margin: 0 auto !important;
  }
}

.x-widget-fallback {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  padding: $spacing-unit * 2;
  font-size: $font-small-size;
  color: $sub-text-color;
  text-align: center;
  background-color: $background-color;
  border: 1px solid #e1e8ed;
  border-radius: 12px;
}

.x-widget-fallback-text {
  margin: 0;
}

.drawer-metrics-section {
  padding: $spacing-unit * 2 0;
  border-bottom: 1px solid $border-color;
}

.drawer-metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $spacing-unit * 2;
}

.drawer-metric-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: center;
}

.drawer-metric-value {
  font-size: 18px;
  font-weight: bold;
  color: $text-color;
}

.drawer-metric-label {
  font-size: 11px;
  color: $sub-text-color;
}

.drawer-campaign-info-section {
  padding: $spacing-unit * 2 0;
  border-bottom: 1px solid $border-color;
}

.drawer-info-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
}

.drawer-info-row {
  display: flex;
  gap: $spacing-unit * 2;
  align-items: flex-start;
  justify-content: space-between;
  font-size: $font-small-size;
}

.drawer-info-label {
  flex-shrink: 0;
  color: $sub-text-color;
}

.drawer-info-value {
  min-width: 0;
  font-weight: 500;
  color: $text-color;
  text-align: right;
  word-break: break-all;
}

.drawer-trend-section {
  padding: $spacing-unit * 2 0;
  border-bottom: 1px solid $border-color;
}

.drawer-section-title {
  margin: 0 0 $spacing-unit;
  font-size: 13px;
  font-weight: bold;
  color: $text-color;
}

.drawer-trend-keyword-cards {
  display: flex;
  flex-direction: column;
  gap: $spacing-unit * 2.5;
}

.drawer-trend-keyword-header {
  display: flex;
  gap: $spacing-unit * 0.5;
  align-items: center;
  margin-bottom: $spacing-unit * 0.5;
}

.drawer-trend-keyword-name {
  font-size: 13px;
  font-weight: bold;
  color: $text-color;
}

.drawer-trend-rank-plain {
  font-size: $font-small-size;
  color: $sub-text-color;
}

.drawer-trend-keyword-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.drawer-trend-row {
  display: flex;
  justify-content: space-between;
  font-size: $font-small-size;
}

.drawer-trend-label {
  color: $sub-text-color;
}

.drawer-trend-value {
  font-weight: 500;
  color: $text-color;
}

.drawer-detail-section {
  padding: $spacing-unit * 2 0;
}

.drawer-detail-text {
  margin: 0;
  font-size: $font-small-size;
  line-height: 1.6;
  color: $text-color;
  white-space: pre-wrap;
}

.drawer-footer {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: $spacing-unit * 1.5;
  align-items: center;
  padding: $spacing-unit * 2 $spacing-unit * 2 $spacing-unit * 2.5;
  background-color: #fff;
  border-top: 1px solid $border-color;
}

.drawer-footer-actions {
  display: flex;
  gap: $spacing-unit * 2;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: $spacing-unit * 2 0;
  background-color: #f0f0f0;
  border-radius: $round;
}

.drawer-footer-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  color: #555;
  cursor: pointer;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgb(0 0 0 / 8%);
  transition: all 0.2s;

  &:hover:not(:disabled) {
    color: $primary-color;
    background-color: #f8f8f8;
    border-color: #ccc;
    box-shadow: 0 2px 6px rgb(0 0 0 / 12%);
  }

  &:disabled {
    color: $border-color;
    cursor: not-allowed;
  }

  i {
    font-size: 22px;
  }
}

.drawer-pagination {
  display: flex;
  gap: $spacing-unit;
  align-items: center;
  justify-content: center;
}

.drawer-pagination-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  font-size: 18px;
  color: $sub-text-color;
  cursor: pointer;
  background: transparent;
  border: none;

  &:hover:not(:disabled) {
    color: $primary-color;
  }

  &:disabled {
    color: $border-color;
    cursor: not-allowed;
  }
}

.drawer-pagination-current {
  font-weight: bold;
  color: $primary-color;
}

.drawer-pagination-separator,
.drawer-pagination-total {
  color: $text-color;
}
</style>
